import React, { useState, useEffect,useCallback  } from "react";
import { io } from "socket.io-client";
import { Container, Row, Modal, Col, Card, Table, Button, Form } from "react-bootstrap";
import { FaShoppingCart, FaUserPlus, FaTimes } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import TopNavbar from "../components/TopNavbar";
import "bootstrap/dist/css/bootstrap.min.css";

// Constants
const OFFLINE_SALES_KEY = 'offline_sales';
const HELD_BILLS_KEY = 'held_bills';
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://localhost:5000';
const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://newpos.gatewaypos.com/api';
const HEALTH_CHECK_INTERVAL = 5000; // 5 seconds
const PING_INTERVAL = 8000; // 8 seconds


const Dashboard = () => {

// State for connection status
const [socket, setSocket] = useState(null);
const [isServerOnline, setIsServerOnline] = useState(false);
const [lastHealthCheck, setLastHealthCheck] = useState(null);
const [pendingOfflineSales, setPendingOfflineSales] = useState(0);
  
  // State Management
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [searchTerm, setSearchTerm] = useState("");
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [user, setUser] = useState(null);
  const [businessName, setBusinessName] = useState("Gateway POS");

  const [heldBills, setHeldBills] = useState([]);           // <-- held bills state
  const [showHeldModal, setShowHeldModal] = useState(false); // <-- held bills modal state
  const [currentHeldId, setCurrentHeldId] = useState(null);  // <-- current held bill ID
  
  // Discount and Payment States
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  
  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [balance, setBalance] = useState(0);
  const [change, setChange] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Customer Management States
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    phone: "",
    address: ""
  });

  const baseURL = process.env.REACT_APP_BASE_URL;
  const token = localStorage.getItem("token");

  
// Health check system
const checkServerHealth = useCallback(async () => {
  try {
    // 1. Check HTTP server
    const httpCheck = await axios.get(`${BASE_URL}/health`, {
      timeout: 2000,
      validateStatus: (status) => status < 500 // Consider 4xx as online
    });

    // 2. Check WebSocket connection
    const wsCheck = socket?.connected || false;
    
    // 3. Verify both are healthy
    const isHealthy = httpCheck.status === 200 && wsCheck;
    
    // 4. Update state only if changed
    setIsServerOnline(prev => {
      if (prev !== isHealthy) {
        console.log(`Server status changed to: ${isHealthy ? 'ONLINE' : 'OFFLINE'}`);
        return isHealthy;
      }
      return prev;
    });
    
    setLastHealthCheck(Date.now());
    return isHealthy;
  } catch (error) {
    console.error('Health check failed:', error);
    setIsServerOnline(false);
    setLastHealthCheck(Date.now());
    return false;
  }
}, [socket]);

useEffect(() => {
  const newSocket = io(SOCKET_URL, {
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
  });
  

  // Connection established handler
  const handleConnect = () => {
    console.log('WebSocket connected');
    checkServerHealth();
  };

  // Connection lost handler
  const handleDisconnect = () => {
    console.log('WebSocket disconnected');
    checkServerHealth();
  };

  // Sale synced confirmation
  const handleSaleSynced = ({ saleId }) => {
    console.log(`Sale ${saleId} synced`);
    removeSyncedSale(saleId);
  };

  // Set up event listeners
  newSocket.on('connect', handleConnect);
  newSocket.on('disconnect', handleDisconnect);
  newSocket.on('sale-synced', handleSaleSynced);

  setSocket(newSocket);

  return () => {
    newSocket.off('connect', handleConnect);
    newSocket.off('disconnect', handleDisconnect);
    newSocket.off('sale-synced', handleSaleSynced);
    newSocket.disconnect();
  };
}, []);
// Add these helper functions to your component
const getOfflineSales = () => {
  return JSON.parse(localStorage.getItem(OFFLINE_SALES_KEY)) || [];
};

const storeOfflineSale = async (saleData) => {
  try {
    const offlineSales = getOfflineSales();
    const saleWithId = {
      ...saleData,
      id: `offline_${Date.now()}`,
      timestamp: new Date().toISOString(),
    };
    offlineSales.push(saleWithId);
    localStorage.setItem(OFFLINE_SALES_KEY, JSON.stringify(offlineSales));
    setPendingOfflineSales(offlineSales.length);
    toast.warning("Sale stored offline. Will sync when connection is restored.");
    return saleWithId;
  } catch (error) {
    console.error("Failed to store offline sale:", error);
    toast.error("Failed to store offline sale!");
    throw error;
  }
};

const removeSyncedSale = (saleId) => {
  const offlineSales = getOfflineSales();
  const updatedSales = offlineSales.filter(sale => sale.id !== saleId);
  localStorage.setItem(OFFLINE_SALES_KEY, JSON.stringify(updatedSales));
  setPendingOfflineSales(updatedSales.filter(s => !s.synced).length);
};




// Periodic health checks
useEffect(() => {
  const interval = setInterval(checkServerHealth, HEALTH_CHECK_INTERVAL);
  return () => clearInterval(interval);
}, [checkServerHealth, HEALTH_CHECK_INTERVAL]); // Stable dependencies

// Offline sales management
// const storeOfflineSale = useCallback(async (saleData) => {
//   try {
//     const offlineSales = JSON.parse(localStorage.getItem(OFFLINE_SALES_KEY)) || [];
//     const saleWithId = {
//       ...saleData,
//       id: `offline_${Date.now()}`,
//       timestamp: new Date().toISOString(),
//     };
//     offlineSales.push(saleWithId);
//     localStorage.setItem(OFFLINE_SALES_KEY, JSON.stringify(offlineSales));
//     setPendingOfflineSales(offlineSales.length);
//     toast.warning("Sale stored offline. Will sync when connection is restored.");
//     return saleWithId;
//   } catch (error) {
//     console.error("Failed to store offline sale:", error);
//     toast.error("Failed to store offline sale!");
//     throw error;
//   }
// }, []);

const syncOfflineSales = useCallback(async () => {
  const offlineSales = getOfflineSales();
  const unsyncedSales = offlineSales.filter(sale => !sale.synced);

  if (!isServerOnline || unsyncedSales.length === 0) return;

  console.log(`Attempting to sync ${unsyncedSales.length} sales`);
  
  try {
    for (const sale of unsyncedSales) {
      try {
        // Try HTTP first
        const response = await axios.post(`${BASE_URL}/sales`, sale, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          timeout: 5000
        });

        console.log("Sync response:", response);

        if (response.status === 201) {
          console.log(`Sale ${sale.id} synced via HTTP`);
          removeSyncedSale(sale.id);
          continue;
        }
      } catch (httpError) {
        console.warn(`HTTP sync failed for sale ${sale.id}:`, httpError);
      }

      // Fall back to WebSocket
      if (socket?.connected) {
        try {
          await new Promise((resolve) => {
            socket.emit('sync-sale', sale, (ack) => {
              if (ack?.success) {
                console.log(`Sale ${sale.id} synced via WebSocket`);
                removeSyncedSale(sale.id);
              }
              resolve();
            });
            
            // Timeout after 5 seconds
            setTimeout(resolve, 5000);
          });
        } catch (wsError) {
          console.warn(`WebSocket sync failed for sale ${sale.id}:`, wsError);
        }
      }
    }

    if (unsyncedSales.length > 0) {
      toast.success("Offline sales synced successfully!");
    }
  } catch (error) {
    console.error('Sync process failed:', error);
  }
}, [isServerOnline, socket]);

// Auto-sync when server comes online
useEffect(() => {
  if (isServerOnline) {
    syncOfflineSales();
  }
}, [isServerOnline, syncOfflineSales]); // Stable dependencies

// Update pending sales count
 useEffect(() => {
  const offlineSales = JSON.parse(localStorage.getItem(OFFLINE_SALES_KEY)) || [];
  setPendingOfflineSales(offlineSales.filter(s => !s.synced).length);
}, []);


useEffect(() => {
  if (!isServerOnline) return;
  const interval = setInterval(() => {
    syncOfflineSales();
  }, 5 * 60 * 1000); // every 5 minutes
  return () => clearInterval(interval);
}, [isServerOnline, syncOfflineSales]);
useEffect(() => {
  if (!isServerOnline) return;
  const interval = setInterval(() => {
    syncProductsFromServer();
  }, 10 * 60 * 1000); // every 10 minutes
  return () => clearInterval(interval);
}, [isServerOnline]);


// Modified handlePayment function
const handlePayment = async () => {
  if (!paymentMethod) {
    toast.warning("Please select a payment method.");
    return;
  }

  setLoading(true);

  try {
    const saleData = {
      total_amount: totalAmount,
      amount_paid: paymentAmount,
      payment_method: paymentMethod,
      change_amount: change,
      balance: balance,
      customer_id: selectedCustomer?.id || null,
      items: cart.map(item => ({
        product_id: item.id,
        quantity: parseFloat(item.qty) || 1,
        price: parseFloat(item.sale_price) || 0,
      })),
    };

    if (isServerOnline) {
      try {
        const response = await axios.post(`${BASE_URL}/sales`, saleData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          timeout: 5000
        });

        if (response.status === 201) {
          handleSuccessfulPayment(response.data.data.sale_id);
          return;
        }
      } catch (onlineError) {
        console.warn("Online sale failed, storing offline:", onlineError);
        setIsServerOnline(false);
      }
    }

    // Offline handling
    const offlineSale = await storeOfflineSale(saleData);
    handleSuccessfulPayment(offlineSale.id);
    
  } catch (error) {
    toast.error(error.response?.data?.message || "Payment failed!");
  } finally {
    setLoading(false);
  }
};

// Modify your handlePayment function
// const handlePayment = async () => {
//   if (!paymentMethod) {
//     toast.warning("Please select a payment method.");
//     return;
//   }

//   setLoading(true);

//   try {
//     const saleData = {
//       total_amount: totalAmount,
//       amount_paid: paymentAmount,
//       payment_method: paymentMethod,
//       change_amount: change,
//       balance: balance,
//       customer_id: selectedCustomer?.id || null,
//       items: cart.map(item => ({
//         product_id: item.id,
//         quantity: parseFloat(item.qty) || 1,
//         price: parseFloat(item.sale_price) || 0,
//       })),
//     };

//     if (isOnline && socket?.connected) {
//       // Try to send directly first
//       try {
//         const response = await axios.post(`${baseURL}/sales`, saleData, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         });

//         if (response.status === 201 || response.status === 200) {
//           handleSuccessfulPayment(response.data.data.sale_id);
//           return;
//         }
//       } catch (onlineError) {
//         console.warn("Online sale failed, storing offline:", onlineError);
//         // Fall through to offline storage
//       }
//     }

//     // If online failed or we're offline, store offline
//     const offlineSale = await storeOfflineSale(saleData);
//     handleSuccessfulPayment(offlineSale.id);
    
//   } catch (error) {
//     toast.error(error.response?.data?.message || "Payment failed!");
//   } finally {
//     setLoading(false);
//   }
// };

// const handleSuccessfulPayment = (saleId) => {
//   toast.success("Sale completed successfully!");
//   setCart([]);
//   setSelectedCustomer(null);
//   setCustomerSearchTerm("");
//   setPaymentMethod("");
//   handleCloseModal();
//   window.open(`/receipt/${saleId}`, "_blank");
// };

const handleSuccessfulPayment = (saleId) => {
  toast.success("Sale completed successfully!");

  // Update offline/local stock for each sold product
 setProducts(prevProducts => {
  const updatedProducts = prevProducts.map(product => {
    const cartItem = cart.find(item => item.id === product.id);
    if (cartItem) {
      // Decrease stock by sold quantity, but not below zero
      return { ...product, stock: Math.max((product.stock || 0) - (cartItem.qty || 0), 0) };
    }
    return product;
  });
  // Save updated products to localStorage for offline persistence
  localStorage.setItem("products", JSON.stringify(updatedProducts));
  return updatedProducts;
});

  setCart([]);
  setSelectedCustomer(null);
  setCustomerSearchTerm("");
  setPaymentMethod("");
  handleCloseModal();
  window.open(`/receipt/${saleId}`, "_blank");
};


  // Theme Management
  useEffect(() => {
    document.body.className = theme;
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(theme === "light" ? "dark" : "light");
  };

  // Data Initialization
  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      const storedUser = localStorage.getItem("login_user");
      if (storedUser) setUser(JSON.parse(storedUser));

      const storedProducts = localStorage.getItem("products");
      if (storedProducts) setProducts(JSON.parse(storedProducts));

      const posSettings = localStorage.getItem("posSettings");
      if (posSettings) {
        const settings = JSON.parse(posSettings);
        setBusinessName(settings?.data?.business_name || "Gateway POS");
      }

      await fetchCustomers();
    } catch (error) {
      console.error("Initialization error:", error);
      toast.error("Failed to initialize data");
    }
  };

  const syncProductsFromServer = async () => {
  try {
    const token = localStorage.getItem("token");
    const response = await axios.get(`${BASE_URL}/products`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (response.status === 200) {
      // Save to localStorage and update state
      localStorage.setItem("products", JSON.stringify(response.data));
      setProducts(response.data.data || response.data); // adjust if your API returns {data: [...]}
      toast.success("Products synced from server!");
    }
  } catch (error) {
    toast.error("Failed to sync products from server.");
    console.error("Product sync error:", error);
  }
};

  // Customer Management
  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${baseURL}/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.status === 200) {
        setCustomers(response.data.data || []);
      }
    } catch (error) {
      console.error("Customer fetch error:", error);
    }
  };

  useEffect(() => {
    if (customerSearchTerm.trim() === "") {
      setFilteredCustomers([]);
      setShowCustomerResults(false);
    } else {
      const filtered = customers.filter(customer => 
        customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
        customer.phone.includes(customerSearchTerm)
      );
      setFilteredCustomers(filtered);
      setShowCustomerResults(true);
    }
  }, [customerSearchTerm, customers]);

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setCustomerSearchTerm(`${customer.name} (${customer.phone})`);
    setShowCustomerResults(false);
  };

  const clearCustomerSelection = () => {
    setSelectedCustomer(null);
    setCustomerSearchTerm("");
    setShowCustomerResults(false);
  };

  const handleCreateCustomer = async () => {
    try {
      const response = await axios.post(`${baseURL}/customers`, newCustomer, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 201) {
        toast.success("Customer created successfully!");
        setCustomers([...customers, response.data.data]);
        setSelectedCustomer(response.data.data);
        setCustomerSearchTerm(`${response.data.data.name} (${response.data.data.phone})`);
        setShowCustomerModal(false);
        setNewCustomer({ name: "", phone: "", address: "" });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create customer");
    }
  };

  // Cart Management
  const addToCart = (product, quantity = 1) => {
  // Prevent adding if stock is zero or less
  if (!product.stock || product.stock < 1) {
    toast.error("This product is out of stock!");
    return;
  }
  setCart(prevCart => {
    const existingItem = prevCart.find(item => item.id === product.id);
    // Prevent adding more than available stock
    const totalQty = (existingItem ? existingItem.qty : 0) + quantity;
    if (totalQty > product.stock) {
      toast.error("Not enough stock available!");
      return prevCart;
    }
    if (existingItem) {
      return prevCart.map(item =>
        item.id === product.id ? { ...item, qty: item.qty + quantity } : item
      );
    }
    return [...prevCart, { ...product, qty: quantity }];
  });
};

  const updateCartItem = (id, field, value) => {
  setCart(prevCart =>
    prevCart.map(item => {
      if (item.id === id) {
        if (field === "qty") {
          // Prevent setting quantity above stock
          const newQty = Math.max(1, Math.min(value, item.stock));
          if (value > item.stock) {
            toast.error("Cannot exceed available stock!");
          }
          return { ...item, qty: newQty };
        }
        return { ...item, [field]: parseFloat(value) || 0 };
      }
      return item;
    })
  );
};

  // Payment Calculations
  useEffect(() => {
    const totalBeforeDiscount = cart.reduce(
      (acc, item) => acc + (item.qty || 0) * (item.sale_price || 0),
      0
    );

    const discount = (discountPercent ? (totalBeforeDiscount * discountPercent) / 100 : 0) + (discountAmount || 0);
    setTotalAmount(totalBeforeDiscount - discount);
    setBalance(totalBeforeDiscount - discount);
  }, [cart, discountPercent, discountAmount]);

  const handlePaymentChange = (e) => {
    const enteredAmount = parseFloat(e.target.value) || 0;
    setPaymentAmount(enteredAmount);

    if (enteredAmount >= totalAmount) {
      setBalance(0);
      setChange(enteredAmount - totalAmount);
    } else {
      setBalance(totalAmount - enteredAmount);
      setChange(0);
    }
  };

  // Sale Processing
  // const handlePayment = async () => {
  //   if (!paymentMethod) {
  //     toast.warning("Please select a payment method.");
  //     return;
  //   }

  //   setLoading(true);

  //   try {
  //     const saleData = {
  //       total_amount: totalAmount,
  //       amount_paid: paymentAmount,
  //       payment_method: paymentMethod,
  //       change_amount: change,
  //       balance: balance,
  //       customer_id: selectedCustomer?.id || null,
  //       items: cart.map(item => ({
  //         product_id: item.id,
  //         quantity: parseFloat(item.qty) || 1,
  //         price: parseFloat(item.sale_price) || 0,
  //       })),
  //     };

  //     const response = await axios.post(`${baseURL}/sales`, saleData, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         "Content-Type": "application/json",
  //       },
  //     });

  //     if (response.status === 201 || response.status === 200) {
  //       toast.success("Sale completed successfully!");
  //       setCart([]);
  //       setSelectedCustomer(null);
  //       setCustomerSearchTerm("");
  //       setPaymentMethod("");
  //       handleCloseModal();
  //       window.open(`/receipt/${response.data.data.sale_id}`, "_blank");
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.message || "Payment failed!");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Keyboard Shortcuts
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Insert") {
        handleShowModal();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, []);

  // Modal Handlers
  const handleShowModal = () => {
    setPaymentAmount(0);
    setBalance(totalAmount);
    setChange(0);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  // Filter products based on search term
  const filteredProducts = searchTerm
    ? products.filter(p =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Calculate totals
  const totalBeforeDiscount = cart.reduce(
    (acc, item) => acc + (item.qty || 0) * (item.sale_price || 0),
    0
  );

  const totalAfterDiscount = totalBeforeDiscount -
    (discountPercent ? (totalBeforeDiscount * discountPercent) / 100 : 0) -
    (discountAmount || 0);

  const hasInvalidStock = cart.some(item => (item.qty || 0) > (item.stock || 0));


  // ===============================
  // Hold bill option new module
  // ================================

  // ---- Held Bills helpers (localStorage) ----
const getHeldBills = () => {
  try { return JSON.parse(localStorage.getItem(HELD_BILLS_KEY)) || []; }
  catch { return []; }
};

const setHeldBillsStorage = (list) => {
  localStorage.setItem(HELD_BILLS_KEY, JSON.stringify(list));
  setHeldBills(list);
};

useEffect(() => {
  setHeldBills(getHeldBills());
}, []);
const handleHoldBill = () => {
  if (!cart?.length) {
    toast.info("Cart is empty.");
    return;
  }

  const hold = {
    id: `hold_${Date.now()}`,
    timestamp: new Date().toISOString(),
    cart: cart.map(i => ({
      id: i.id,
      product_name: i.product_name,
      sale_price: i.sale_price,
      qty: i.qty,
      stock: i.stock ?? 0
    })),
    selectedCustomer: selectedCustomer ? {
      id: selectedCustomer.id,
      name: selectedCustomer.name,
      phone: selectedCustomer.phone,
      address: selectedCustomer.address
    } : null,
    discountPercent,
    discountAmount,
    // preserve precomputed totals you show on the right
    totals: {
      before: cart.reduce((a, it) => a + (it.qty||0)*(it.sale_price||0), 0),
      after: (cart.reduce((a, it) => a + (it.qty||0)*(it.sale_price||0), 0)
             - (discountPercent ? (cart.reduce((a, it) => a + (it.qty||0)*(it.sale_price||0), 0) * discountPercent)/100 : 0)
             - (discountAmount || 0))
    }
  };

  const list = getHeldBills();
  list.push(hold);
  setHeldBillsStorage(list);

  // Clear current UI so till can proceed to next person
  setCart([]);
  setSelectedCustomer(null);
  setCustomerSearchTerm("");
  setDiscountPercent(0);
  setDiscountAmount(0);
  setCurrentHeldId(null);

  toast.success("Bill held. You can resume it anytime.");
};
const resumeHeldBill = (holdId) => {
  const list = getHeldBills();
  const hold = list.find(h => h.id === holdId);
  if (!hold) { toast.error("Held bill not found."); return; }

  // Restore UI state
  setCart(hold.cart.map(r => ({
    id: r.id,
    product_name: r.product_name,
    sale_price: r.sale_price,
    qty: r.qty,
    stock: r.stock
  })));
  if (hold.selectedCustomer) {
    setSelectedCustomer(hold.selectedCustomer);
    setCustomerSearchTerm(`${hold.selectedCustomer.name} (${hold.selectedCustomer.phone || ""})`);
  } else {
    setSelectedCustomer(null);
    setCustomerSearchTerm("");
  }
  setDiscountPercent(hold.discountPercent || 0);
  setDiscountAmount(hold.discountAmount || 0);

  setCurrentHeldId(hold.id);
  setShowHeldModal(false);
  toast.success("Held bill resumed.");
};

const deleteHeldBill = (holdId) => {
  const list = getHeldBills().filter(h => h.id !== holdId);
  setHeldBillsStorage(list);
  if (currentHeldId === holdId) setCurrentHeldId(null);
  toast.info("Held bill deleted.");
};

if (currentHeldId) {
  const remaining = getHeldBills().filter(h => h.id !== currentHeldId);
  setHeldBillsStorage(remaining);
  setCurrentHeldId(null);
}




  return (
    <div className={`dashboard ${theme}`}>
      <Container fluid className={`vh-100 ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
        <TopNavbar user={user} toggleTheme={toggleTheme} theme={theme} />
        
        <Row className="mt-3">
          {/* Product Section */}
          <Col md={8}>
            <Row className="mb-2">
              <Col>
                <Form.Control type="text" placeholder="Barcode Scan" />
              </Col>
              <Col className="position-relative">
                <Form.Control
                  type="text"
                  placeholder="Search Item..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {filteredProducts.length > 0 && (
                  <Card className="position-absolute w-100 mt-1 shadow" style={{ zIndex: 10 }}>
                    <ul className="list-group">
                      {filteredProducts.map((product) => (
                        // <li
                        //   key={product.id}
                        //   className={`list-group-item list-group-item-action${product.stock < 1 ? " disabled text-danger" : ""}`}
                        //   onClick={() => product.stock > 0 && addToCart(product)}
                        //   style={{ cursor: product.stock > 0 ? "pointer" : "not-allowed" }}
                        // >
                        //   {product.product_name} - Rs {product.sale_price}
                        //   {product.stock < 1 && <span className="ms-2 badge bg-danger">Out of Stock</span>}
                        // </li>
                        <li
                          key={product.id}
                          className={`list-group-item list-group-item-action${product.stock < 1 ? " disabled text-danger" : ""}`}
                          onClick={() => {
                            if (product.stock > 0) {
                              addToCart(product);
                              setSearchTerm(""); // <-- Clear search input after adding
                            }
                          }}
                          style={{ cursor: product.stock > 0 ? "pointer" : "not-allowed" }}
                        >
                          {product.product_name} - Rs {product.sale_price}
                          {product.stock < 1 && <span className="ms-2 badge bg-danger">Out of Stock</span>}
                        </li>
                      ))}
                    </ul>
                  </Card>
                )}
              </Col>
              <Col md="auto">
                <div className="connection-status">
                  <span className={`badge ${isServerOnline ? 'bg-success' : 'bg-danger'}`}>
                    {isServerOnline ? 'Server Online' : 'Server Offline'}
                  </span>
                  {pendingOfflineSales > 0 && (
                    <span className="pending-sales ms-2">
                      ({pendingOfflineSales} pending)
                    </span>
                  )}
                  <Button
  variant="outline-success"
  size="sm"
  className="ms-2"
  onClick={syncProductsFromServer}
  disabled={!isServerOnline}
>
  Sync Products
</Button>
                </div>
              </Col>
            

            </Row>

            {/* Cart Table */}
            <Card className={`shadow ${theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}`}>
              <Card.Body>
                <Table bordered hover responsive className={theme === "dark" ? "table-dark" : ""}>
                  <thead className={theme === "dark" ? "bg-secondary text-white" : "bg-primary text-white"}>
                    <tr>
                      <th>Sr.</th>
                      <th>Name</th>
                      <th>Qty</th>
                      <th>Stock</th>
                      <th>Price</th>
                      <th>Total</th>
                      <th>Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cart.length > 0 ? (
                      cart.map((item, index) => (
                        <tr key={item.id}>
                          <td>{index + 1}</td>
                          <td>{item.product_name}</td>
                          <td>
                            <Form.Control
                              type="number"
                              className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                              value={item.qty}
                              min="1"
                              onChange={(e) => updateCartItem(item.id, "qty", Number(e.target.value))}
                            />
                          </td>
                          <td>{item.stock}</td>
                          <td>
                            <Form.Control
                              type="number"
                              className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                              value={item.sale_price}
                              min="1"
                              onChange={(e) => updateCartItem(item.id, "sale_price", Number(e.target.value))}
                            />
                          </td>
                          <td>Rs {item.qty * item.sale_price}</td>
                          <td>
                            <Button variant="danger" size="sm" onClick={() => setCart(cart.filter((p) => p.id !== item.id))}>
                              X
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className={theme === "dark" ? "bg-secondary text-white border-0 text-center" : "text-center"}>
                          No Items Added
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>

          {/* Billing Section */}
          <Col md={4}>
            <Card className={`shadow p-3 transition-colors duration-300 ${
              theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"
            }`}>
              <h5 className="text-success fw-bold">Billing Details</h5>
              <hr />
              
              {/* Customer Search */}
              <Form.Group className="mb-3">
                <Form.Label>Customer (Optional)</Form.Label>
                <div className="position-relative">
                  <div className="input-group">
                    <Form.Control
                      type="text"
                      placeholder="Search customer by name or phone"
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      onFocus={() => customerSearchTerm && setShowCustomerResults(true)}
                    />
                    {selectedCustomer && (
                      <Button 
                        variant="outline-danger" 
                        onClick={clearCustomerSelection}
                      >
                        <FaTimes />
                      </Button>
                    )}
                    <Button 
                      variant="outline-primary" 
                      onClick={() => setShowCustomerModal(true)}
                    >
                      <FaUserPlus />
                    </Button>
                  </div>
                  
                  {/* Customer search results */}
                  {showCustomerResults && filteredCustomers.length > 0 && (
                    <Card className="position-absolute w-100 mt-1 shadow" style={{ zIndex: 10 }}>
                      <ul className="list-group">
                        {filteredCustomers.map(customer => (
                          <li
                            key={customer.id}
                            className="list-group-item list-group-item-action"
                            onClick={() => handleCustomerSelect(customer)}
                            style={{ cursor: "pointer" }}
                          >
                            <div><strong>{customer.name}</strong></div>
                            <div>{customer.phone}</div>
                            <div className="small text-muted">{customer.address}</div>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  )}
                </div>
                
                {/* Selected customer details */}
                {selectedCustomer && (
                  <div className="mt-2 small">
                    <div><strong>Phone:</strong> {selectedCustomer.phone}</div>
                    <div><strong>Address:</strong> {selectedCustomer.address}</div>
                  </div>
                )}
              </Form.Group>

              <h4 className="fw-bold">Total: Rs {totalAfterDiscount.toFixed(2)}</h4>
              <p>Sub-Total: Rs {totalBeforeDiscount.toFixed(2)}</p>
              <p>Discount: Rs {(totalBeforeDiscount - totalAfterDiscount).toFixed(2)}</p>

              <Form.Group className="mb-2">
                <Form.Label>Discount (%)</Form.Label>
                <Form.Control
                  type="number"
                  value={discountPercent}
                  onChange={(e) => setDiscountPercent(Number(e.target.value))}
                />
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label>Discount (Rs)</Form.Label>
                <Form.Control
                  type="number"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(Number(e.target.value))}
                />
              </Form.Group>

              <div className="d-grid gap-2">
                
                <Button variant="success">
                  <FaShoppingCart /> New Order
                </Button>
                {/* <Button variant="primary">
                  <FaUserPlus /> New Customer
                </Button> */}
                <Button variant="warning" onClick={handleHoldBill}>
                  Hold Bill
                </Button>

                <Button variant="secondary" onClick={() => setShowHeldModal(true)}>
                  Held Bills ({heldBills.length})
                </Button>

                <Button onClick={handleShowModal} variant="info">
                  Payment (Insert)
                </Button>
              </div>

              <p className="mt-3 text-center text-muted">
                {businessName} -{" "}
                {user ? user.user_name.charAt(0).toUpperCase() + user.user_name.slice(1).toLowerCase() : "Guest"}
              </p>
            </Card>
          </Col>
        </Row>

        {/* Payment Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered>
          <Modal.Header closeButton className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Modal.Title>Billing Information</Modal.Title>
          </Modal.Header>
          <Modal.Body className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Payment Method</Form.Label>
                <Form.Select 
                  value={paymentMethod} 
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                >
                  <option value="">Select Payment Method</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Total Amount</Form.Label>
                <Form.Control 
                  type="text" 
                  value={`£${totalAfterDiscount.toFixed(2)}`} 
                  readOnly 
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Amount Paid</Form.Label>
                <Form.Control 
                  type="number"
                  value={paymentAmount}
                  onChange={handlePaymentChange}
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Change</Form.Label>
                <Form.Control 
                  type="text" 
                  value={`£${change.toFixed(2)}`} 
                  readOnly 
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Button 
              variant={theme === "dark" ? "outline-light" : "outline-dark"} 
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button 
              variant={theme === "dark" ? "light" : "dark"} 
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? "Processing..." : "Confirm Payment"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* New Customer Modal */}
        <Modal show={showCustomerModal} onHide={() => setShowCustomerModal(false)} centered>
          <Modal.Header closeButton className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Modal.Title>Add New Customer</Modal.Title>
          </Modal.Header>
          <Modal.Body className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control 
                  type="text" 
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Phone *</Form.Label>
                <Form.Control 
                  type="text" 
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Address</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3}
                  value={newCustomer.address}
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  className={theme === "dark" ? "bg-secondary text-white border-0" : ""}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
            <Button 
              variant={theme === "dark" ? "outline-light" : "outline-dark"} 
              onClick={() => setShowCustomerModal(false)}
            >
              Cancel
            </Button>
            <Button 
              variant={theme === "dark" ? "light" : "dark"} 
              onClick={handleCreateCustomer}
              disabled={!newCustomer.name || !newCustomer.phone}
            >
              Save Customer
            </Button>
          </Modal.Footer>
        </Modal>
        {/* Held Bills Modal */}
<Modal show={showHeldModal} onHide={() => setShowHeldModal(false)} centered size="lg">
  <Modal.Header closeButton className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
    <Modal.Title>Held Bills</Modal.Title>
  </Modal.Header>
  <Modal.Body className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
    {heldBills.length === 0 ? (
      <p className="text-muted mb-0">No held bills.</p>
    ) : (
      <Table bordered hover responsive className={theme === "dark" ? "table-dark" : ""}>
        <thead className={theme === "dark" ? "bg-secondary text-white" : "bg-primary text-white"}>
          <tr>
            <th>Ref</th>
            <th>Customer</th>
            <th>Items</th>
            <th>Total (Rs)</th>
            <th>Held At</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {heldBills.map(h => (
            <tr key={h.id}>
              <td>{h.id}</td>
              <td>{h.selectedCustomer?.name || "Walk-in"}</td>
              <td>{h.cart?.length || 0}</td>
              <td>{Number(h.totals?.after ?? 0).toFixed(2)}</td>
              <td>{new Date(h.timestamp).toLocaleString()}</td>
              <td className="d-flex gap-2">
                <Button size="sm" variant="primary" onClick={() => resumeHeldBill(h.id)}>
                  Resume
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => deleteHeldBill(h.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )}
  </Modal.Body>
  <Modal.Footer className={theme === "dark" ? "bg-dark text-white" : "bg-light text-dark"}>
    <Button variant={theme === "dark" ? "outline-light" : "outline-dark"} onClick={() => setShowHeldModal(false)}>
      Close
    </Button>
  </Modal.Footer>
</Modal>

      </Container>
    </div>
  );
};

export default Dashboard;